import { EmojiPicker as Picker, type EmojiPickerListCategoryHeaderProps, type EmojiPickerListEmojiProps, type EmojiPickerListRowProps } from "frimousse";
import { createContext, useContext, useId, useState } from "react";
import { Icon } from "./Icon.tsx";

function CategoryHeader({ category, ...props }: EmojiPickerListCategoryHeaderProps) {
  return (
    <div className="b-emoji-category" {...props}>
      {category.label}
    </div>
  );
}

function Row({ children, ...props }: EmojiPickerListRowProps) {
  return (
    <div className="b-emoji-row" {...props}>
      {children}
    </div>
  );
}

function EmojiButton({ emoji, ...props }: EmojiPickerListEmojiProps) {
  return (
    <button type="button" className="b-emoji-button" {...props}>
      {emoji.emoji}
    </button>
  );
}

const LIST_COMPONENTS = { CategoryHeader, Row, Emoji: EmojiButton };

/**
 * "Frequently used" (Slack): my emoji, best first, shown above the grid, and told about every pick. Provided by the
 * workspace shell (EmojiUsageViewModel); absent, the picker has no such row.
 */
export type FrequentEmoji = {
  emoji: readonly string[];
  label: string;
  onUse: (emoji: string, report: boolean) => void;
  /** My skin tone (Slack's picker preference) and how to change it; absent = the default tone, no selector. */
  skinTone?: SkinTone;
  onSkinTone?: (tone: SkinTone) => void;
  /** Translated name of the skin tone button ("Skin tone"). */
  skinToneLabel?: string;
};

type SkinTone = "none" | "light" | "medium-light" | "medium" | "medium-dark" | "dark";

/** The emoji the tones are shown on (raised hand, like Slack): content, not a UI glyph. */
const TONE_SAMPLE = String.fromCodePoint(0x270b);

/** Slack's skin tone button: the hand in my tone; opens the six hands to choose from. */
function SkinTonePicker({ tone, label, onChange }: { tone: SkinTone; label: string; onChange: (tone: SkinTone) => void }) {
  const [open, setOpen] = useState(false);
  return (
    <Picker.SkinTone emoji={TONE_SAMPLE}>
      {({ skinToneVariations, setSkinTone }) => {
        const current = skinToneVariations.find((v) => v.skinTone === tone)?.emoji ?? TONE_SAMPLE;
        return open ? (
          <div className="b-emoji-tones" role="radiogroup" aria-label={label}>
            {skinToneVariations.map((v) => (
              <button
                key={v.skinTone}
                type="button"
                role="radio"
                aria-checked={v.skinTone === tone}
                aria-label={v.emoji}
                className="b-emoji-button"
                data-testid={`emoji-skin-tone-${v.skinTone}`}
                onClick={() => {
                  setSkinTone(v.skinTone);
                  onChange(v.skinTone);
                  setOpen(false);
                }}
              >
                {v.emoji}
              </button>
            ))}
          </div>
        ) : (
          <button type="button" className="b-emoji-button" aria-label={label} title={label} data-testid="emoji-skin-tone" onClick={() => setOpen(true)}>
            {current}
          </button>
        );
      }}
    </Picker.SkinTone>
  );
}
export const FrequentEmojiContext = createContext<FrequentEmoji | null>(null);

/** Emoji shown in the "Frequently used" row (two rows of eight). */
const FREQUENT_SHOWN = 16;

export type EmojiPickerProps = {
  onSelect: (emoji: string) => void;
  /** Translated search placeholder. */
  searchPlaceholder?: string;
  locale?: "en" | "pt-BR";
  /** Take the container's size instead of the default 312 × 340 panel (the emoji / stickers / GIFs picker). */
  fill?: boolean;
  /** Report picks to the server as uses (composer: yes; reaction pickers: no, adding a reaction counts by itself). */
  report?: boolean;
  testID?: string;
};

/** Searchable emoji grid (frimousse on web; the system emoji picker natively). Emoji are content, never UI icons. */
export function EmojiPicker({ onSelect: select, searchPlaceholder, locale = "en", fill, report = true, testID }: EmojiPickerProps) {
  const id = useId();
  const frequent = useContext(FrequentEmojiContext);
  const onSelect = (emoji: string) => {
    frequent?.onUse(emoji, report);
    select(emoji);
  };
  return (
    <Picker.Root
      className={fill === true ? "b-emoji b-emoji-fill" : "b-emoji"}
      locale={locale === "pt-BR" ? "pt" : "en"}
      columns={8}
      skinTone={frequent?.skinTone ?? "none"}
      onEmojiSelect={(e) => onSelect(e.emoji)}
      data-testid={testID}
    >
      {/* The search is drawn by TextField's styles (outlined, small): same radius, border, focus ring and placeholder. */}
      <div className="b-emoji-search">
        <div className="b-field b-field-outlined">
          {searchPlaceholder === undefined ? null : (
            <label className="b-visually-hidden" htmlFor={id}>
              {searchPlaceholder}
            </label>
          )}
          <div className="b-field-box">
            <Icon name="search" size="icon.sm" color="text.tertiary" />
            <Picker.Search id={id} className="b-field-input" placeholder={searchPlaceholder} autoFocus data-testid={testID === undefined ? undefined : `${testID}-search`} />
          </div>
        </div>
        {frequent?.onSkinTone === undefined ? null : (
          <SkinTonePicker tone={frequent.skinTone ?? "none"} label={frequent.skinToneLabel ?? ""} onChange={frequent.onSkinTone} />
        )}
      </div>
      {frequent === null || frequent.emoji.length === 0 ? null : (
        <div className="b-emoji-frequent" data-testid={testID === undefined ? undefined : `${testID}-frequent`}>
          <div className="b-emoji-category">{frequent.label}</div>
          <div className="b-emoji-frequent-grid">
            {frequent.emoji.slice(0, FREQUENT_SHOWN).map((e) => (
              <button key={e} type="button" className="b-emoji-button" onClick={() => onSelect(e)}>
                {e}
              </button>
            ))}
          </div>
        </div>
      )}
      <Picker.Viewport className="b-emoji-viewport">
        <Picker.Loading className="b-emoji-status" />
        <Picker.Empty className="b-emoji-status" />
        <Picker.List className="b-emoji-list" components={LIST_COMPONENTS} />
      </Picker.Viewport>
    </Picker.Root>
  );
}
